"use client";

/**
 * PasswordProtectionForm
 * Displays a password entry form for password-protected landing pages
 */

import { useState } from "react";

interface PasswordProtectionFormProps {
  onSubmit: (password: string) => void;
  error?: string | null;
  isLoading?: boolean;
}

export default function PasswordProtectionForm({ onSubmit, error, isLoading }: PasswordProtectionFormProps) {
  // States
  const [password, setPassword] = useState("");

  // Handlers
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(password);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  // Render
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-md p-8 max-w-md w-full">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Required</h1>
        <p className="text-gray-600 mb-6">This page is password protected. Please enter the password to continue.</p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-400 focus:border-transparent ${
                error ? "border-red-300 bg-red-50" : "border-gray-300"
              }`}
              placeholder="Enter password"
              autoFocus
              disabled={isLoading}
              aria-label="Password"
              tabIndex={0}
            />
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading || !password}
            className="w-full px-6 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            tabIndex={0}
          >
            {isLoading ? "Verifying..." : "Enter"}
          </button>
        </form>
      </div>
    </div>
  );
}
